import React from 'react'
import { Link } from 'react-router-dom'
import { Layout } from '../shared/components'
import { useCssHandles } from '../hooks/useCssHandles'
import { colors, borderRadius, shadows } from '../styles/design-system'

const CSS_HANDLES = [
  'notFound',
  'container',
  'content',
  'badge',
  'badgeCode',
  'title',
  'description',
  'actions',
  'primaryAction',
  'secondaryAction',
  'linkList',
  'linkItem',
  'supportCard',
  'supportTitle',
  'supportDescription',
  'supportLink'
] as const

type QuickLinkConfig = {
  id: string
  to: string
  title: string
  description: string
}

const QUICK_LINKS: QuickLinkConfig[] = [
  {
    id: 'catalog',
    to: '/catalogo',
    title: 'Catálogo completo',
    description: 'Acesse todas as opções de baterias disponíveis.'
  },
  {
    id: 'support',
    to: '/sobre',
    title: 'Fale com a equipe',
    description: 'Envie uma solicitação e responda com nosso suporte.'
  }
]

const NotFound: React.FC = () => {
  const handles = useCssHandles(CSS_HANDLES)

  return (
    <Layout>
      <main
        className={`${handles.notFound} flex min-h-[calc(100vh-160px)] flex-col justify-center px-4 py-16 sm:px-6 lg:px-8`}
        style={{
          background: `linear-gradient(135deg, ${colors.secondary.lightBlue}, ${colors.neutral.gray50})`
        }}
      >
        <div className={`${handles.container} mx-auto flex w-full max-w-5xl flex-col gap-10`}>
          <section
            className={`${handles.content} grid gap-10 rounded-3xl bg-white/95 p-10 shadow-xl backdrop-blur-sm md:grid-cols-[1.1fr_0.9fr]`}
            style={{
              borderRadius: borderRadius['2xl'],
              boxShadow: shadows.xl
            }}
          >
            <div className="space-y-8">
              <span
                className={`${handles.badge} inline-flex w-fit items-center gap-3 rounded-full px-4 py-2 text-sm font-semibold`}
                style={{
                  backgroundColor: colors.secondary.lightBlue,
                  color: colors.primary.blue,
                  borderRadius: borderRadius.full
                }}
              >
                <span aria-hidden="true" className={`${handles.badgeCode} flex h-9 w-9 items-center justify-center rounded-full bg-white text-lg font-semibold`}>
                  404
                </span>
                Página não encontrada
              </span>

              <div className="space-y-4">
                <h1 className={`${handles.title} text-4xl font-semibold text-gray-900 sm:text-5xl`}>
                  Ops, este caminho está sem energia
                </h1>
                <p className={`${handles.description} text-lg text-gray-600 sm:text-xl`}>
                  O conteúdo que você procura pode ter sido movido ou não existe mais. Escolha um dos caminhos abaixo para continuar a jornada com a Baterpower.
                </p>
              </div>

              <div className={`${handles.actions} flex flex-col gap-3 sm:flex-row`}>
                <Link
                  to="/"
                  className={`${handles.primaryAction} inline-flex items-center justify-center rounded-2xl px-6 py-3 text-base font-semibold text-white transition hover:brightness-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2`}
                  style={{
                    borderRadius: borderRadius['2xl'],
                    backgroundColor: colors.primary.blue,
                    boxShadow: shadows.lg
                  }}
                >
                  Voltar para o início
                </Link>
                <Link
                  to="/catalogo"
                  className={`${handles.secondaryAction} inline-flex items-center justify-center rounded-2xl px-6 py-3 text-base font-semibold transition hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2`}
                  style={{
                    borderRadius: borderRadius['2xl'],
                    backgroundColor: colors.secondary.lightBlue,
                    color: colors.primary.blue,
                    boxShadow: shadows.md
                  }}
                >
                  Abrir catálogo
                </Link>
              </div>

              <div className="space-y-3">
                <p className="text-sm font-semibold text-gray-700">Acesso rápido</p>
                <ul className={`${handles.linkList} grid gap-3 sm:grid-cols-2`}>
                  {QUICK_LINKS.map(link => (
                    <li key={link.id}>
                      <Link
                        to={link.to}
                        className={`${handles.linkItem} group flex flex-col gap-1 rounded-2xl border border-gray-100 bg-gray-50/80 p-4 transition hover:-translate-y-0.5 hover:border-yellow-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2`}
                        style={{
                          borderRadius: borderRadius['2xl'],
                          boxShadow: shadows.sm
                        }}
                      >
                        <span className="text-sm font-semibold text-gray-900">{link.title}</span>
                        <span className="text-sm text-gray-600">{link.description}</span>
                        <span className="text-xs font-medium text-blue-900 opacity-0 transition group-hover:opacity-100">
                          Continuar →
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="flex flex-col justify-between gap-6">
              <div
                aria-hidden="true"
                className="relative flex w-full justify-center"
                style={{
                  borderRadius: borderRadius['2xl'],
                  background: `linear-gradient(160deg, ${colors.primary.yellow}22, ${colors.primary.blue}11)`
                }}
              >
                <svg
                  aria-hidden="true"
                  focusable="false"
                  viewBox="0 0 320 220"
                  className="h-auto w-full max-w-sm p-8"
                >
                  <defs>
                    <linearGradient id="batteryGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor={colors.primary.blue} stopOpacity="0.9" />
                      <stop offset="100%" stopColor={colors.primary.yellow} stopOpacity="0.9" />
                    </linearGradient>
                  </defs>
                  <rect x="30" y="60" width="240" height="100" rx="20" fill="url(#batteryGradient)" />
                  <rect x="270" y="92" width="20" height="36" rx="6" fill={colors.primary.blue} opacity="0.8" />
                  <rect x="60" y="85" width="40" height="50" rx="10" fill={colors.neutral.white} opacity="0.9" />
                  <path d="M80 100v20" stroke={colors.primary.blue} strokeWidth="4" strokeLinecap="round" />
                  <path d="M220 110h20" stroke={colors.neutral.white} strokeWidth="6" strokeLinecap="round" />
                  <circle cx="150" cy="110" r="16" fill={colors.neutral.white} opacity="0.85" />
                  <path
                    d="M146 112h12l-5 10 1-8h-8l5-10-1 8z"
                    fill={colors.primary.blue}
                    opacity="0.9"
                  />
                </svg>
              </div>

              <div
                className={`${handles.supportCard} flex flex-col gap-4 rounded-3xl bg-white/90 p-8 shadow-md`}
                style={{
                  borderRadius: borderRadius['2xl'],
                  boxShadow: shadows.md
                }}
              >
                <div className="flex items-start gap-4">
                  <span
                    aria-hidden="true"
                    className="flex h-12 w-12 items-center justify-center rounded-2xl"
                    style={{
                      backgroundColor: `${colors.primary.yellow}33`,
                      color: colors.primary.blue
                    }}
                  >
                    <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" className="h-7 w-7">
                      <path
                        d="M12 2a7 7 0 0 0-7 7v4.82l-.94 1.88A1 1 0 0 0 5 17h14a1 1 0 0 0 .88-1.47L19 13.82V9a7 7 0 0 0-7-7zm0 20a3 3 0 0 0 3-3H9a3 3 0 0 0 3 3z"
                        fill="currentColor"
                      />
                    </svg>
                  </span>
                  <div className="space-y-2">
                    <h2 className={`${handles.supportTitle} text-xl font-semibold text-gray-900`}>Podemos ajudar?</h2>
                    <p className={`${handles.supportDescription} text-sm text-gray-600`}>
                      Fale com a nossa equipe para recomendar a bateria ideal para o seu cenário ou tirar dúvidas rápidas.
                    </p>
                  </div>
                </div>
                <div className="flex flex-col gap-3 text-sm text-gray-700 sm:flex-row sm:items-center sm:justify-between">
                  <div className="space-y-1">
                    <p className="font-semibold text-gray-900">Central de suporte</p>
                    <p className="text-gray-600">Segunda à sábado — 08h às 18h</p>
                  </div>
                  <a
                    href="tel:+5511999999999"
                    className="inline-flex items-center justify-center rounded-2xl px-5 py-3 font-medium text-blue-900 transition hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                    style={{
                      borderRadius: borderRadius['2xl'],
                      backgroundColor: colors.secondary.lightBlue
                    }}
                  >
                    (11) 99999-9999
                  </a>
                </div>
                <a
                  href="mailto:contato@baterpowerbaterias.com.br"
                  className={`${handles.supportLink} inline-flex items-center gap-2 text-sm font-medium text-blue-900 transition hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2`}
                >
                  <span aria-hidden="true">↗</span>
                  contato@baterpowerbaterias.com.br
                </a>
              </div>
            </div>
          </section>
        </div>
      </main>
    </Layout>
  )
}

export default NotFound;